<script setup lang="ts">
import { mergeRules } from 'core/services/form'
import { useField } from 'vee-validate'

const { max = 999999, min = 0, name, rules, step = 1, requiredError } = defineProps<{
  max?: number
  min?: number
  placeholder?: string
  step?: number
  name: string
  rules?: any
  currency: string
  mobileVersion?: boolean
  requiredError?: string
}>()

const emit = defineEmits(['update:modelValue'])

const appConfig = useAppConfig()
const mergedRules = computed(() => mergeRules(rules, requiredError))
const { value, handleChange } = useField<any>(name, mergedRules)
const inputValue = ref(value.value?.toLocaleString(appConfig.locale.code) || '')
let valueWatcher: WatchStopHandle | null
function createValueWatcher() {
  valueWatcher = watch(value, (val: any) => {
    inputValue.value !== val ? (inputValue.value = val.toLocaleString(appConfig.locale.code)) : (inputValue.value = val)
  })
}
function destroyValueWatcher() {
  if (valueWatcher) {
    valueWatcher()
    valueWatcher = null
  }
}
createValueWatcher()
function validate() {
  let number = Number(inputValue.value.replace(/[^0-9,]+/g, '')) || 0
  if (number > max) number = max
  inputValue.value = number.toLocaleString(appConfig.locale.code)
}
function check() {
  const number = inputValue.value ? Number(inputValue.value.replace(/[^0-9.-]+/g, '')) : handleChange(min || 0)
  if (min && number < min) {
    inputValue.value = min.toLocaleString(appConfig.locale.code)
    handleChange(min)
    emit('update:modelValue', min)
  }
  else {
    handleChange(number)
    emit('update:modelValue', number)
  }
}

function addValue(val: number) {
  let number = Number(inputValue.value.replace(/[^0-9.-]+/g, '')) + val
  if (number < min) number = min
  inputValue.value = number.toLocaleString(appConfig.locale.code)
  validate()
  destroyValueWatcher()
  handleChange(number)
  emit('update:modelValue', number)
  nextTick(() => createValueWatcher())
}

onMounted(() => {
  validate()
})
</script>

<template>
  <div class="w-full">
    <div class="flex items-center gap-1 xs:gap-2.5" :class="mobileVersion ? 'w-full' : '<sm:w-full'">
      <BaseButton
        render="link"
        class="px-3.5 py-3.5 xs:px-4 xs:py-4"
        :class="{ 'sm:hidden': !mobileVersion }"
        :test-id="`${name}MinusButton`"
        @click="addValue(-step)"
      >
        <AppIcon :size="13" icon="far-minus" class="text-primary" />
      </BaseButton>
      <FormFieldText
        disable-storyblok
        :name="name"
        :mask="{ mask: Number, thousandsSeparator: ' ' }"
        :suffix="currency"
        @change="check()"
        @input="validate()"
      />
      <BaseButton
        render="link"
        class="px-3.5 py-3.5 xs:px-4 xs:py-4"
        :class="{ 'sm:hidden': !mobileVersion }"
        :test-id="`${name}PlusButton`"
        @click="addValue(step)"
      >
        <AppIcon :size="13" icon="far-plus" class="text-primary" />
      </BaseButton>
    </div>
  </div>
</template>
